import { useNavigate, useParams } from "react-router";
import { BackArrowIcon } from "vega-icons";

import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import type { ApiDealShortlistResponse } from "@vega-api/generated/types/apiDealShortlistResponse";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { LpNdaTable } from "../components/LpNdaTable/LpNdaTable";
import { LpNdaUploadSection } from "../components/LpNdaUploadSection/LpNdaUploadSection";
import { useDealName } from "../hooks/useDealName";

export const CoInvestmentLpNdaPage = () => {
  const { id: dealId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const { data: hasLps, isLoading: isShortlistLoading } = useGetDealShortlist(
    dealId ?? "",
    undefined,
    {
      query: {
        enabled: isTruthy(dealId),
        select: ({ data }: ApiDealShortlistResponse) =>
          (data?.dealShortlistItems.length ?? 0) > 0,
      },
    },
  );

  if (!dealId) {
    return null;
  }

  const handleBackToDataRoom = () => {
    navigate(CoInvestmentFullPaths.CoInvestmentDataRoom.replace(":id", dealId));
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-12">
        <ThemedIconButton
          dataTestId="back-to-data-room-button"
          icon={<BackArrowIcon />}
          onClick={handleBackToDataRoom}
          size="medium"
          type="button"
        />
        <div className="flex-1">
          <CoInvestmentPageHeader
            fundName={dealName}
            isLoading={isDealNameLoading}
            showDrawer={false}
            title={m.coInvestments_manageLpSpecificNdas()}
          />
        </div>
      </div>

      {isShortlistLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : null}
      {!isShortlistLoading && !hasLps ? (
        <EmptyCard
          description={m.coInvestments_manageLpSpecificNdas_noLps()}
          title={m.coInvestments_manageLpSpecificNdas_noLps_title()}
        />
      ) : null}
      {!isShortlistLoading && hasLps ? (
        <>
          <LpNdaUploadSection dealId={dealId} />
          <LpNdaTable dealId={dealId} />
        </>
      ) : null}
    </>
  );
};
